import React, { useContext } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { Button } from '@rneui/themed';
import { format, parseISO, isSameDay } from 'date-fns';
import PropTypes from 'prop-types';
import { generateRecurrences } from '../../../common/helpers/Event';
import { AddToCalendar } from './AddToCalendar';
import { Details } from './Details';
import { Header } from './Header';
import LayoutContext from '../../../contexts/Layout';

const Event = ({
    ctaButtonText,
    ctaUrl,
    endDateAndTime,
    eventDescription,
    eventTitle,
    eventTitleColor = '#fff',
    followContentLink = async () => {},
    maxRecurringDate,
    recurs,
    startDateAndTime,
    width,
    nodeTypeAlias = 'Event',
    ...rest
}) => {
    const { layout } = useContext(LayoutContext);
    const ctaText = ctaButtonText && ctaButtonText.length > 0 ? ctaButtonText : undefined;
    let recurrences;

    const renderCtaButton = () => {
        if (ctaText) {
            return (
                <Button
                    buttonStyle={Styles.cta}
                    containerStyle={{ marginRight: 10 }}
                    onPress={async () => await followContentLink(ctaUrl.isExternal, ctaUrl.internalKey, ctaUrl.externalUrl)}
                    title={ctaText}
                    titleProps={{ numberOfLines: 1 }}
                    titleStyle={Styles.ctaTitle}
                    type='solid'
                />
            );
        }

        return null;
    };

    const renderButtons = includeMargins => {
        if (layout.device.isMobile) {
            return (
                <View
                    style={{
                        flexDirection: 'row',
                        marginHorizontal: includeMargins ? 10 : undefined,
                        marginTop: layout.styleGuide.spacer3
                    }}
                    dataSet={{ spacerTop: `StyleGuide.Spacer3(${layout.styleGuide.spacer3})` }}
                >
                    <View style={{ marginBottom: layout.styleGuide.spacer3 }} dataSet={{ spacerBottom: `StyleGuide.Spacer3(${layout.styleGuide.spacer3})` }}>
                        {renderCtaButton()}
                    </View>
                    <View>
                        <AddToCalendar
                            endDateAndTime={endDateAndTime}
                            eventDescription={eventDescription}
                            eventTitle={eventTitle}
                            recurs={recurs}
                            startDateAndTime={startDateAndTime}
                            {...rest}
                        />
                    </View>
                </View>
            );
        } else {
            return (
                <View
                    style={{
                        flexDirection: 'row',
                        marginHorizontal: includeMargins ? 10 : undefined,
                        marginTop: layout.styleGuide.spacer3
                    }}
                    dataSet={{ spacerTop: `StyleGuide.Spacer3(${layout.styleGuide.spacer3})` }}
                >
                    {renderCtaButton()}

                    <AddToCalendar
                        endDateAndTime={endDateAndTime}
                        eventDescription={eventDescription}
                        eventTitle={eventTitle}
                        recurs={recurs}
                        startDateAndTime={startDateAndTime}
                        {...rest}
                    />
                </View>
            );
        }
    };

    const renderForSameDay = () => {
        const actualEndDateTime = maxRecurringDate.equals('0001-01-01T00:00:00') ? endDateAndTime : maxRecurringDate;
        const start = typeof startDateAndTime === 'string' ? parseISO(startDateAndTime) : new Date(startDateAndTime);
        const end = typeof actualEndDateTime === 'string' ? parseISO(actualEndDateTime) : new Date(actualEndDateTime);

        return isSameDay(start, end)
            ? layout.device.isMobile
                ? format(start, "EEE, MMM do yyyy")
                : format(start, "EEEE, MMMM do yyyy")
            : layout.device.isMobile
            ? `${format(start, "EEE, MMM do yyyy")} - ${format(end, "EEE, MMM do yyyy")}`
            : `${format(start, "EEEE, MMMM do yyyy")} - ${format(end, "EEEE, MMMM do yyyy")}`;
    };

    if (recurs > 0) {
        const occurs = generateRecurrences({
            startDateAndTime,
            maxRecurringDate,
            ...rest
        });

        if (occurs?.length > 0) {
            recurrences = occurs;
        }
    }

    return (
        <View
            style={{ marginBottom: layout.styleGuide.componentSeparator }}
            dataSet={{ umbracoComponentName: nodeTypeAlias, spacerBottom: `StyleGuide.ComponentSeparator(${layout.styleGuide.componentSeparator})` }}
        >
            <View>
                <Header
                    eventDescription={eventDescription}
                    eventTitle={eventTitle}
                    eventTitleColor={eventTitleColor}
                    renderButtons={renderButtons}
                    sameDay={renderForSameDay(true)}
                    width={width}
                    {...rest}
                />

                <Details
                    endDateAndTime={endDateAndTime}
                    eventDescription={eventDescription}
                    recurrences={recurrences}
                    sameDay={renderForSameDay(false)}
                    startDateAndTime={startDateAndTime}
                    width={width}
                    {...rest}
                />

                {renderButtons(true)}
            </View>
        </View>
    );
};

const Styles = StyleSheet.create({
    buttonContainer: {
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'flex-start'
    },
    cta: {
        backgroundColor: '#a61a2b',
        paddingVertical: 10
    },
    ctaTitle: {
        color: '#fff',
        fontFamily: 'GothamBook',
        fontSize: Platform.OS.equals('web') ? '16pt' : 16
    }
});

Event.propTypes = {
    featuredImage: PropTypes.shape({
        cdnImagePath: PropTypes.string
    }),
    followContentLink: PropTypes.func,
    campuse: PropTypes.arrayOf(PropTypes.string),
    ctaButtonText: PropTypes.string,
    ctaUrl: PropTypes.shape({
        externalUrl: PropTypes.string,
        internalKey: PropTypes.string,
        isExternal: PropTypes.bool
    }),
    endDateAndTime: PropTypes.string,
    eventDescription: PropTypes.string,
    eventTitleColor: PropTypes.string,
    maxRecurringDate: PropTypes.string,
    ministry: PropTypes.arrayOf(PropTypes.string),
    recurs: PropTypes.number,
    startDateAndTime: PropTypes.string.isRequired
};

export default Event;
